import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { normalizeLanguageTag } from './core/utils/language';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from './core/utils/storage';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'everest-smart-routes';
  view: string = '';
  path: string = '';
  showHeader = true;
  showFooter = true;
  showBreadcrumb = true;
  headerExpanded = false;
  translationsLoaded = false;
  BREADCRUMB_EXCEPTIONS = ['/login','/home','/cookie-policy','/legal-disclaimer'];
  private readonly RESERVED_VIEWS = new Set([
    'home',
    'login',
    'charts',
    'users',
    'devices',
    'legal-disclaimer',
    'cookie-policy'
  ]);
  
  private subscriptions: Subscription = new Subscription();
  private readonly LANG_STORAGE_KEY = 'everest.lang.v1';

  constructor(private router: Router, private translocoSrv: TranslocoService) {
  }
  ngOnInit(): void {
    const defaultLang = normalizeLanguageTag(this.translocoSrv.getDefaultLang(), 'es');
    const storedLang = safeLocalStorageGetItem(this.LANG_STORAGE_KEY);
    const desiredLang = normalizeLanguageTag(storedLang, defaultLang);

    this.subscriptions.add(
      this.translocoSrv.langChanges$.subscribe((lang) => {
        safeLocalStorageSetItem(this.LANG_STORAGE_KEY, normalizeLanguageTag(lang, defaultLang));
      })
    );

    this.subscriptions.add(
      this.translocoSrv.load(desiredLang).subscribe({
        next: () => {
          // Avoid transient "key flashing" (e.g. 'date.0') by only switching
          // the active language after the translation file has loaded.
          if (this.translocoSrv.getActiveLang() !== desiredLang) {
            this.translocoSrv.setActiveLang(desiredLang);
          }
          this.translationsLoaded = true;
        },
        error: (err) => {
          console.error('Error al cargar traducciones:', err);
          // Fall back to default language to keep the app usable.
          this.subscriptions.add(
            this.translocoSrv.load(defaultLang).subscribe({
              next: () => {
                if (this.translocoSrv.getActiveLang() !== defaultLang) {
                  this.translocoSrv.setActiveLang(defaultLang);
                }
                this.translationsLoaded = true;
              },
              error: (fallbackErr) => {
                console.error('Error al cargar traducciones (fallback):', fallbackErr);
                this.translationsLoaded = true;
              }
            })
          );
        },
    }));
    this.subscriptions.add(
      this.router.events.subscribe(() => {
        const rawUrl = this.router.url || '';
        const path = rawUrl.split('?')[0].split('#')[0];
        const segments = path.split('/').filter(Boolean);

        this.showHeader = !path.includes('/login');
        this.showFooter = !path.includes('/login');
        // this.headerExpanded = this.router.url.includes('/home');
        this.showBreadcrumb = !this.BREADCRUMB_EXCEPTIONS.some(value => path.includes(value));
        this.path = path;
        this.view = this.resolveViewFromSegments(segments);
    }));
  }
  
  private resolveViewFromSegments(segments: string[]): string {
    if (!segments || segments.length === 0) {
      return '';
    }

    const first = segments[0];
    if (this.RESERVED_VIEWS.has(first)) {
      return first;
    }

    // Dynamic hierarchy routes:
    // - /<contentTypeId>
    // - /<contentTypeId>/<entityId>[/<contentTypeId>/<entityId>]...
    if (segments.length === 1) {
      return first;
    }
    if (segments.length % 2 === 0) {
      return segments[segments.length - 2] || first;
    }
    // Trailing typeId means "ALL" at that level.
    return segments[segments.length - 1] || first;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
